import React, { useState, useCallback, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ConnectionLineType,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Play,
  Save,
  Settings,
  Plus,
  Clock,
  BarChart3,
  GitBranch,
  BookTemplate,
  Zap,
  Database,
  Brain,
  FileText,
  Bell,
  Code,
  Globe,
  Filter,
  RefreshCw,
  AlertTriangle,
  Calendar,
  History,
  Download,
  Upload,
  Copy,
  Trash2,
  Eye,
  Edit3,
  Share2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Import custom node components
import StartNode from '../components/workflow/nodes/StartNode';
import EndNode from '../components/workflow/nodes/EndNode';
import ActionNode from '../components/workflow/nodes/ActionNode';
import DecisionNode from '../components/workflow/nodes/DecisionNode';
import DatabaseNode from '../components/workflow/nodes/DatabaseNode';
import AINode from '../components/workflow/nodes/AINode';
import NotificationNode from '../components/workflow/nodes/NotificationNode';
import FileNode from '../components/workflow/nodes/FileNode';
import APINode from '../components/workflow/nodes/APINode';
import LoopNode from '../components/workflow/nodes/LoopNode';

// Import modals
import TemplatesModal from '../components/workflow/modals/TemplatesModal';
import AnalyticsModal from '../components/workflow/modals/AnalyticsModal';
import ScheduleModal from '../components/workflow/modals/ScheduleModal';
import VersionsModal from '../components/workflow/modals/VersionsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const API = `${BACKEND_URL}/api`;

const nodeTypes = {
  start: StartNode,
  end: EndNode,
  action: ActionNode,
  decision: DecisionNode,
  database: DatabaseNode,
  ai: AINode,
  notification: NotificationNode,
  file: FileNode,
  api: APINode,
  loop: LoopNode,
};

const initialNodes = [
  {
    id: '1',
    type: 'start',
    position: { x: 250, y: 25 },
    data: { label: 'Start' },
  },
];

const initialEdges = [];

const WorkflowBuilder = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNodeType, setSelectedNodeType] = useState('action');
  const [isRunning, setIsRunning] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [showWorkflowList, setShowWorkflowList] = useState(false);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: { 
          label: `${type.charAt(0).toUpperCase() + type.slice(1)} Node`,
          config: {}
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const saveWorkflow = async () => {
    try {
      const workflowData = {
        name: currentWorkflow?.name || `Workflow ${new Date().toLocaleString()}`,
        description: currentWorkflow?.description || 'Generated workflow',
        nodes: nodes,
        edges: edges,
        config: {
          autoSave: true,
          version: (currentWorkflow?.config?.version || 0) + 1
        }
      };

      const response = await fetch(`${API}/workflows`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workflowData),
      });

      if (!response.ok) throw new Error('Failed to save workflow');
      
      const savedWorkflow = await response.json();
      setCurrentWorkflow(savedWorkflow);
      toast.success('Workflow saved successfully!');
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error('Failed to save workflow');
    }
  };

  const runWorkflow = async () => {
    if (nodes.length === 0) {
      toast.error('Add some nodes to run the workflow');
      return;
    }

    setIsRunning(true);
    try {
      const response = await fetch(`${API}/workflows/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nodes: nodes,
          edges: edges,
        }),
      });

      if (!response.ok) throw new Error('Failed to execute workflow');
      
      const result = await response.json();
      toast.success(`Workflow executed successfully! ID: ${result.executionId}`);
    } catch (error) {
      console.error('Error running workflow:', error);
      toast.error('Failed to execute workflow');
    } finally {
      setIsRunning(false);
    }
  };

  const clearWorkflow = () => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setCurrentWorkflow(null);
    toast.info('Workflow cleared');
  };

  const nodeToolbox = [
    { type: 'action', icon: Zap, label: 'Action', color: 'bg-blue-500' },
    { type: 'decision', icon: Filter, label: 'Decision', color: 'bg-yellow-500' },
    { type: 'database', icon: Database, label: 'Database', color: 'bg-green-500' },
    { type: 'ai', icon: Brain, label: 'AI/ML', color: 'bg-purple-500' },
    { type: 'file', icon: FileText, label: 'File Ops', color: 'bg-orange-500' },
    { type: 'notification', icon: Bell, label: 'Notify', color: 'bg-red-500' },
    { type: 'api', icon: Globe, label: 'API Call', color: 'bg-indigo-500' },
    { type: 'loop', icon: RefreshCw, label: 'Loop', color: 'bg-pink-500' },
    { type: 'end', icon: AlertTriangle, label: 'End', color: 'bg-gray-500' },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Toolbar */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Code className="h-7 w-7 text-blue-600 mr-3" />
              Agentic AI Workflow Builder
            </h1>
            <div className="text-sm text-gray-500">
              {currentWorkflow ? `Editing: ${currentWorkflow.name}` : 'New Workflow'}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Main Action Buttons */}
            <button
              onClick={() => setShowTemplatesModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <BookTemplate className="h-4 w-4" />
              <span>Templates</span>
            </button>

            <button
              onClick={() => setShowAnalyticsModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <BarChart3 className="h-4 w-4" />
              <span>Analytics</span>
            </button>

            <button
              onClick={() => setShowScheduleModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <Clock className="h-4 w-4" />
              <span>Schedule</span>
            </button>

            <button
              onClick={() => setShowVersionsModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <GitBranch className="h-4 w-4" />
              <span>Versions</span>
            </button>

            <div className="w-px h-6 bg-gray-300" />

            <button
              onClick={saveWorkflow}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>Save</span>
            </button>

            <button
              onClick={runWorkflow}
              disabled={isRunning}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              {isRunning ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              <span>{isRunning ? 'Running...' : 'Run'}</span>
            </button>

            <button
              onClick={clearWorkflow}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Left Sidebar - Node Toolbox */}
        <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Nodes</h3>
          
          <div className="space-y-2">
            {nodeToolbox.map((node) => {
              const Icon = node.icon;
              return (
                <div
                  key={node.type}
                  className={`${node.color} p-3 rounded-lg cursor-move hover:opacity-80 transition-opacity`}
                  draggable
                  onDragStart={(event) => onDragStart(event, node.type)}
                >
                  <div className="flex items-center space-x-3 text-white">
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{node.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Node Information */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Instructions</h4>
            <p className="text-xs text-gray-600">
              Drag nodes from this panel to the canvas to build your workflow. 
              Connect nodes by dragging from connection points.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-700 mb-2">Current Workflow</h4>
            <div className="space-y-1 text-xs text-blue-600">
              <div>Nodes: {nodes.length}</div>
              <div>Connections: {edges.length}</div>
              <div>Status: {isRunning ? 'Running' : 'Ready'}</div>
            </div>
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            connectionLineType={ConnectionLineType.SmoothStep}
            fitView
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
            
            {/* Canvas Overlay Panel */}
            <Panel position="top-right" className="m-4">
              <div className="bg-white rounded-lg shadow-lg p-4 space-y-2">
                <div className="text-sm font-medium text-gray-700">Canvas Controls</div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowWorkflowList(!showWorkflowList)}
                    className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                    title="Workflow List"
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                  <button
                    className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                    title="Export"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                    title="Import"
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Panel>
          </ReactFlow>
        </div>
      </div>

      {/* Modals */}
      {showTemplatesModal && (
        <TemplatesModal
          onClose={() => setShowTemplatesModal(false)}
          onSelectTemplate={(template) => {
            setNodes(template.nodes);
            setEdges(template.edges);
            setCurrentWorkflow(template);
            setShowTemplatesModal(false);
            toast.success(`Loaded template: ${template.name}`);
          }}
        />
      )}

      {showAnalyticsModal && (
        <AnalyticsModal
          onClose={() => setShowAnalyticsModal(false)}
          workflowId={currentWorkflow?.id}
        />
      )}

      {showScheduleModal && (
        <ScheduleModal
          onClose={() => setShowScheduleModal(false)}
          workflow={currentWorkflow}
          onScheduled={(schedule) => {
            toast.success(`Workflow scheduled: ${schedule.frequency}`);
          }}
        />
      )}

      {showVersionsModal && (
        <VersionsModal
          onClose={() => setShowVersionsModal(false)}
          workflowId={currentWorkflow?.id}
          onRestoreVersion={(version) => {
            setNodes(version.nodes);
            setEdges(version.edges);
            setCurrentWorkflow(version);
            setShowVersionsModal(false);
            toast.success(`Restored version ${version.version}`);
          }}
        />
      )}
    </div>
  );
};

export default WorkflowBuilder;